import { useState, useEffect, useCallback } from 'react';
import { chatService } from '@services/chatService';
import { ChatAgentType } from '@constants/chat-agents';
import { CHATBOT_INSTRUCTIONS_SYNCED } from '@utils/chatbotOpenRequests';

interface UseChatSuggestionsOptions {
  activeAgent: ChatAgentType;
  token: string | null;
}

interface UseChatSuggestionsReturn {
  commands: any[];
  isCommandsLoading: boolean;
  quickFunctions: any[];
  quickFunctionSearchList: any[];
  instructions: any[];
  quickSelectionData: { title: string; items: any[]; type: 'INSTRUCTION' | 'QUICK_FUNCTION'; isLoading?: boolean } | null;
  setQuickSelectionData: (data: any) => void;
  handleInstructionClick: () => Promise<void>;
  handleQuickFunctionClick: () => Promise<void>;
  closeQuickSelection: () => void;
}

/**
 * Hook chuyên quản lý việc kéo các dữ liệu tĩnh từ API (Gợi ý lệnh, Chức năng nhanh)
 * Giúp giảm tải cho useChat.ts
 */
export const useChatSuggestions = ({ activeAgent, token }: UseChatSuggestionsOptions): UseChatSuggestionsReturn => {
  const [commandsCache, setCommandsCache] = useState<Record<ChatAgentType, any[]>>({
    [ChatAgentType.AI]: [],
    [ChatAgentType.CSKH]: [],
    [ChatAgentType.TAX_QUESTION]: [],
    [ChatAgentType.INSTRUCTION_MANUAL]: [],
    [ChatAgentType.ANALYTICS_QUESTION]: [],
  });
  const [commandsLoadingCache, setCommandsLoadingCache] = useState<Record<ChatAgentType, boolean>>({
    [ChatAgentType.AI]: false,
    [ChatAgentType.CSKH]: false,
    [ChatAgentType.TAX_QUESTION]: false,
    [ChatAgentType.INSTRUCTION_MANUAL]: false,
    [ChatAgentType.ANALYTICS_QUESTION]: false,
  });
  
  const commands = commandsCache[activeAgent] || [];
  const isCommandsLoading = commandsLoadingCache[activeAgent] || false;
  
  const [quickFunctions, setQuickFunctions] = useState<any[]>([]);
  const [quickFunctionSearchList, setQuickFunctionSearchList] = useState<any[]>([]);
  const [instructions, setInstructions] = useState<any[]>([]);
  
  const [quickSelectionData, setQuickSelectionData] = useState<{ 
    title: string; 
    items: any[]; 
    type: 'INSTRUCTION' | 'QUICK_FUNCTION'; 
    isLoading?: boolean 
  } | null>(null);

  // Fetch default quick functions on mount (used for the quick function dropdown button)
  useEffect(() => {
    const fetchQuickFunctionsDefault = async () => {
      const currentToken = token || sessionStorage.getItem('access_token');
      if (currentToken) {
        try {
          const res = await chatService.getQuickFunctionsDefault(currentToken);
          if (res?.data) {
            setQuickFunctions(res.data);
          }
        } catch (error) {
          console.error('Failed to fetch default quick functions:', error);
        }
      }
    };
    fetchQuickFunctionsDefault();
  }, [token]);

  // Fetch full quick function list on mount (used for keyword auto-complete search while typing)
  useEffect(() => {
    const fetchQuickFunctionSearchList = async () => {
      const currentToken = token || sessionStorage.getItem('access_token');
      if (currentToken) {
        try {
          const res = await chatService.getQuickFunctions(currentToken);
          if (res?.data) {
            setQuickFunctionSearchList(res.data);
          }
        } catch (error) {
          console.error('Failed to fetch quick functions for auto-complete:', error);
        }
      }
    };
    fetchQuickFunctionSearchList();
  }, [token]);

  const fetchInstructions = useCallback(async () => {
    const currentToken = token || sessionStorage.getItem('access_token');
    if (!currentToken) return;

    try {
      const res = await chatService.getInstructionSuggestions(currentToken);
      const suggestion = res?.data?.suggestion ?? res?.suggestion;
      if (Array.isArray(suggestion)) {
        setInstructions(suggestion);
      }
    } catch (error) {
      console.error('Failed to fetch instructions for auto-complete:', error);
    }
  }, [token]);

  // Token / first load
  useEffect(() => {
    void fetchInstructions();
  }, [fetchInstructions]);

  // Kết quả instruction-manual từ runChatbotOpenRequests (mỗi lần mở chatbot)
  useEffect(() => {
    const onSynced = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      if (Array.isArray(detail)) {
        setInstructions(detail);
      }
    };
    window.addEventListener(CHATBOT_INSTRUCTIONS_SYNCED, onSynced);
    return () => window.removeEventListener(CHATBOT_INSTRUCTIONS_SYNCED, onSynced);
  }, []);

  // Fetch default commands khi đổi Agent
  useEffect(() => {
    const fetchCommands = async () => {
      const currentToken = token || sessionStorage.getItem('access_token');
      if (currentToken) {
        // Chỉ set loading nếu chưa có dữ liệu cache cho agent này (tránh nháy)
        if (commandsCache[activeAgent].length === 0) {
          setCommandsLoadingCache(prev => ({ ...prev, [activeAgent]: true }));
        }

        try {
          const res = await chatService.getDefaultCommands(currentToken, activeAgent);
          const commandData = res?.data?.data || res?.data || res;
          if (Array.isArray(commandData)) {
            const mappedCommands = commandData.map((cmd: any) => ({
              ...cmd,
              icon: cmd.imageUrl || cmd.icon,
              iconBackground: cmd.imageUrl || cmd.iconBackground,
            }));
            setCommandsCache(prev => ({ ...prev, [activeAgent]: mappedCommands }));
          } else {
            setCommandsCache(prev => ({ ...prev, [activeAgent]: [] }));
          }
        } catch (error) {
          console.error('Failed to fetch default commands:', error);
          setCommandsCache(prev => ({ ...prev, [activeAgent]: [] }));
        } finally {
          setCommandsLoadingCache(prev => ({ ...prev, [activeAgent]: false }));
        }
      } else {
        setCommandsLoadingCache(prev => ({ ...prev, [activeAgent]: false }));
      }
    };
    fetchCommands();
  }, [activeAgent, token]);

  // Action Click Handlers
  const handleInstructionClick = async () => {
    const currentToken = token || sessionStorage.getItem('access_token');
    if (!currentToken) return;

    setQuickSelectionData({
      title: 'Hướng dẫn sử dụng',
      items: [],
      type: 'INSTRUCTION',
      isLoading: true
    });

    try {
      const res = await chatService.getInstructionSuggestions(currentToken);
      const suggestion = res?.data?.suggestion ?? res?.suggestion;
      if (suggestion) {
        setQuickSelectionData({
          title: 'Hướng dẫn sử dụng',
          items: suggestion.slice(0, 10),
          type: 'INSTRUCTION',
          isLoading: false
        });
      } else {
        setQuickSelectionData(null);
      }
    } catch (error) {
      console.error('Failed to fetch instructions:', error);
      setQuickSelectionData(null);
    }
  };

  const handleQuickFunctionClick = async () => {
    const currentToken = token || sessionStorage.getItem('access_token');
    if (!currentToken) return;

    setQuickSelectionData({
      title: 'Mở nhanh tính năng',
      items: [],
      type: 'QUICK_FUNCTION',
      isLoading: true
    });

    try {
      const res = await chatService.getQuickFunctions(currentToken);
      if (res?.data) {
        setQuickSelectionData({
          title: 'Mở nhanh tính năng',
          items: res.data.slice(0, 10),
          type: 'QUICK_FUNCTION',
          isLoading: false
        });
      } else {
        setQuickSelectionData(null);
      }
    } catch (error) {
      console.error('Failed to fetch quick functions:', error);
      setQuickSelectionData(null);
    }
  };

  const closeQuickSelection = () => {
    setQuickSelectionData(null);
  };

  return {
    commands,
    isCommandsLoading,
    quickFunctions,
    quickFunctionSearchList,
    instructions,
    quickSelectionData,
    setQuickSelectionData,
    handleInstructionClick,
    handleQuickFunctionClick,
    closeQuickSelection
  };
};

export default useChatSuggestions;
